import PropTypes from 'prop-types'
import React from 'react'
import getID from '../../utils/getCloudinaryID'
import { Helmet } from 'react-helmet'
import { getTermHeader } from '../../domain/term/headerMappings/getTermHeader'
import { CustomHeaderType } from '../../domain/term/headerMappings/constants'
import BreadCrumbV2 from '../molecules/BreadCrumb/BreadCrumbV2'
import {
  isInsurancePage,
  isInsurancePageType,
  isLocationPageType,
  isFacetPageType
} from '../../domain/useCases/pageTypeCheck'
import WhyTrustRecoveryMobile from './WhyTrustRecoveryMobile'
import { isLocationInUnitedStates } from '../../domain/location/useCases/isLocationInUnitedStates'
import { getHeroDescription } from '../../domain/hero/getHeroDescription'
import { SIGNIFICANT_CENTER_COUNT } from '../../domain/hero/constants'
import generateCustomHeroCopy from '../../domain/seo/useCases/hero/generateCustomHeroCopy'
import { getPrecedingArticle } from '../../utils/text'
import { isNonEmptyString } from '../../utils/checks'
import {
  insuranceLayout,
  INSURANCE_DEFAULT_ID
} from '../../constants/insuranceLayout'
import { InsuranceChip } from '@/components/molecules/InsuranceChip/InsuranceChip'
import { getDescriptionToRender } from '../../domain/hero/getDescriptionToRender'
import { stateNameToCode } from '../../utils/stateCode'

const MAX_TITLE_LENGTH = 50

const lowercaseTitle = (title) => {
  return title ? title.toLowerCase() : ''
}

const HeroV2 = (props) => {
  const {
    title,
    background,
    count = '',
    pageType,
    secondaryTerm,
    customTitle,
    shouldDefaultRegionalize,
    customTermHeader,
    breadcrumbs,
    facetLocation,
    location,
    center,
    term
  } = props

  const id = getID(background)
  const pageIsInsuranceType = isInsurancePage(pageType)

  const baseImageUrl = `${process.env.GATSBY_CLOUDINARY_PROXY_BASE_URL}/media/upload/ar_3:2,w_600,h_400,q_90,f_auto,c_fill,g_auto`

  const srcImg = `${baseImageUrl}/${
    isInsurancePageType(pageType) ? INSURANCE_DEFAULT_ID : id
  }`

  const shouldUseInsuranceColor =
    pageIsInsuranceType && isNonEmptyString(insuranceLayout[term?.slug]?.style)

  const heroHeader = getTermHeader({
    pageType,
    title,
    termTitle: secondaryTerm,
    customTermHeader,
    headerType: CustomHeaderType.HERO
  })

  const parentLocation = location?.parentLocation || ''

  const formattedParentLocation =
    stateNameToCode[parentLocation] || parentLocation

  const locationForHero =
    location?.type === 'City' ? `${title}, ${formattedParentLocation}` : title

  const lowerCaseTerm = lowercaseTitle(secondaryTerm)
  const lowerCaseTitle = lowercaseTitle(title)

  const isUSLocation = isLocationInUnitedStates(location)
  const description = getHeroDescription(
    isUSLocation,
    count,
    title,
    shouldDefaultRegionalize,
    location
  )

  const customHeroCopy = generateCustomHeroCopy({
    term: term?.title || secondaryTerm,
    count,
    customHeroCopy: location?.customHeroCopy || term?.customHeroCopy,
    location: locationForHero,
    pageType
  })

  const copy = {
    Location: {
      title:
        count > 0
          ? `Drug & Alcohol Rehabs in ${title}`
          : `Drug & Alcohol Rehabs near ${title}`,
      description: customHeroCopy || description
    },
    special_consideration: {
      title: `Best ${title} Rehabs`,
      description: `Our independent research team continuously gathers and evaluates data to compile an unbiased and thorough list of the <a style="text-decoration: underline;" href="#browse-all"> ${
        count > SIGNIFICANT_CENTER_COUNT ? count : ''
      } best ${lowerCaseTitle} treatment centers</a>. Review our curated list to find the best rehab that meets your recovery needs.`
    },
    activity: {
      title: `Best Rehabs with ${title}`,
      description: `Our research team continuously gathers and evaluates data to compile an unbiased and thorough list of the <a style="text-decoration: underline;" href="#browse-all"> ${
        count > SIGNIFICANT_CENTER_COUNT ? count : ''
      } best treatment centers</a> offering ${lowerCaseTitle}.`
    },
    accepted_insurance: {
      title: `Best Rehabs with ${title} Insurance Coverage`,
      description: `<span class='font-semibold'>Yes, ${title} covers treatment for addiction and mental health</span>. Our independent research team compiled information on <a style="text-decoration: underline;" href="#browse-all"> ${
        count > SIGNIFICANT_CENTER_COUNT ? count : ''
      } centers that accept ${title}</a>. Find the best rehab aligned with your needs by filtering by location, condition, substance, therapies, and approach.`
    },
    treatment_therapy: {
      title: `Best Rehabs with ${title}`,
      description: `Our research team evaluated the <a style="text-decoration: underline;" href="#browse-all"> ${
        count > SIGNIFICANT_CENTER_COUNT ? count : ''
      } best treatment centers</a> with ${lowerCaseTitle} programs. These centers incorporate ${lowerCaseTitle} into their treatment plans to support individuals recovering from addiction or mental health conditions. Review our curated list to find the best rehab that meets your needs.`
    },
    treatment_services: {
      title: `Best ${title} Centers`,
      description: `Our research team evaluated the <a style="text-decoration: underline;" href="#browse-all"> ${
        count > SIGNIFICANT_CENTER_COUNT ? count : ''
      } best treatment centers for ${lowerCaseTitle}</a>. ${title} is one of many treatment services available to support you in your recovery journey. Review our curated list to find the best rehab center that meets your needs.`
    },
    amenities: {
      title: `Best Rehabs with ${title}`,
      description: `<strong>The best rehab is one that meets your needs.</strong> Our research team evaluated the <a style="text-decoration: underline;" href="#browse-all">${
        count > SIGNIFICANT_CENTER_COUNT
          ? `${count} best treatment centers`
          : 'best treatment centers'
      } with ${getPrecedingArticle(
        title
      )} ${lowerCaseTitle}</a>. Review our curated list of centers providing treatment for addiction and mental health conditions such as depression, anxiety, trauma, and more.`
    },
    setting: {
      title: `Best Rehabs with ${title}`,
      description: `Our research team continuously gathers and evaluates data to compile an unbiased and thorough list of the <a style="text-decoration: underline;" href="#browse-all"> ${
        count > SIGNIFICANT_CENTER_COUNT ? count : ''
      } best treatment centers</a> with ${lowerCaseTitle}.`
    },
    treatment_method: {
      title: `Best ${title} Rehabs`,
      description: `Our research team evaluated the <a style="text-decoration: underline;" href="#browse-all"> ${
        count > SIGNIFICANT_CENTER_COUNT ? count : ''
      } best ${lowerCaseTitle} treatment centers</a>. ${title} programs can treat addiction as well as address mental health conditions like depression, anxiety, trauma, and more. Review our curated list to find the best rehab that meets your recovery needs.`
    },
    what_we_treat: {
      title: `Best ${title} Rehabs`,
      description: `Our independent research team evaluated the <a style="text-decoration: underline;" href="#browse-all"> ${
        count > SIGNIFICANT_CENTER_COUNT ? count : ''
      } best treatment centers for ${lowerCaseTitle}</a>. Review our curated list of centers offering care options such as virtual, outpatient, and residential treatment. Read unbiased reviews to find the best treatment center for your recovery needs.`
    },
    price: {
      title: `Best ${title} Rehabs`,
      description: `Our research team continuously gathers and evaluates data to compile an unbiased and thorough list of the <a style="text-decoration: underline;" href="#browse-all"> ${
        count > SIGNIFICANT_CENTER_COUNT ? count : ''
      } best treatment centers</a> for ${lowerCaseTitle}.`
    },
    who_we_treat: {
      title: `Best ${title} Rehabs`,
      description: `Our research team evaluated the <a style="text-decoration: underline;" href="#browse-all"> ${
        count > SIGNIFICANT_CENTER_COUNT ? count : ''
      } best treatment centers for ${lowerCaseTitle}</a>. Here you can find rehab centers treating addiction, as well as addressing mental health conditions like depression, anxiety, trauma, and more. Read unbiased reviews to find the best rehab center for your recovery needs.`
    },
    'Location+price': {
      title: `Best ${secondaryTerm} Rehabs in ${title}`,
      description: `<span class='font-semibold'>The best rehab is one that meets your needs.</span> Our research team compiled information on the <a style="text-decoration: underline;" href="#browse-all"> ${
        count > SIGNIFICANT_CENTER_COUNT ? count : ''
      } best treatment centers for ${lowerCaseTerm} in ${locationForHero}.</a>`
    },
    'Location+what_we_treat': {
      title: `Best ${secondaryTerm} Rehabs in ${title}`,
      description: `Our independent research team evaluated the <a style="text-decoration: underline;" href="#browse-all"> ${
        count > SIGNIFICANT_CENTER_COUNT ? count : ''
      } best treatment centers for ${lowerCaseTerm}</a> in ${locationForHero}. Review our curated list of centers offering care options such as virtual, outpatient, and residential treatment. Read unbiased reviews to find the best treatment center for your recovery needs.`
    },
    'Location+who_we_treat': {
      title: `Best ${secondaryTerm} Rehabs in ${title}`,
      description: `Our research team evaluated the <a style="text-decoration: underline;" href="#browse-all">${
        count > SIGNIFICANT_CENTER_COUNT ? count : ''
      } best treatment centers for ${lowerCaseTerm}</a> in ${locationForHero}. Here you can find rehab centers treating addiction, as well as addressing mental health conditions like depression, anxiety, trauma, and more.
Read unbiased reviews to find the best rehab center for your recovery needs.`
    },
    'Location+accepted_insurance': {
      title: `Best Rehabs with ${title} Insurance Coverage`,
      description: `<span class='font-semibold'>Yes, ${secondaryTerm} covers treatment for addiction and mental health.</span> Our independent research team compiled information on the <a style="text-decoration: underline;" href="#browse-all">${
        count > SIGNIFICANT_CENTER_COUNT ? count : ''
      } centers in ${locationForHero} that accept ${secondaryTerm}</a>. Find the rehab best aligned with your needs by filtering by location, condition, substance, therapies, and approach.`
    },
    'Location+special_considerations': {
      title: `Best ${secondaryTerm} Rehabs in ${title}`,
      description: `Our independent research team continuously gathers and evaluates data to compile an unbiased and thorough list of the <a style="text-decoration: underline;" href="#browse-all">${
        count > SIGNIFICANT_CENTER_COUNT ? count : ''
      } best ${lowerCaseTerm} treatment centers</a> in ${locationForHero}. Review our curated list to find the best rehab that meets your recovery needs.`
    },
    'Location+amenities': {
      title: `Best ${secondaryTerm} Rehabs in ${title}`,
      description: `<strong>The best rehab is one that meets your needs.</strong> Our research team evaluated the <a style="text-decoration: underline;" href="#browse-all">${
        count > SIGNIFICANT_CENTER_COUNT ? count : ''
      } best rehab centers with ${getPrecedingArticle(
        lowerCaseTerm
      )} ${lowerCaseTerm}</a> in ${locationForHero}. Review our curated list of centers providing treatment for addiction and mental health conditions such as depression, anxiety, trauma, and more.`
    },
    'Location+treatment_therapies': {
      title: `Best ${secondaryTerm} Rehabs in ${title}`,
      description: `Our research team evaluated the <a style="text-decoration: underline;" href="#browse-all">${
        count > SIGNIFICANT_CENTER_COUNT ? count : ''
      } best treatment centers</a> with ${lowerCaseTerm} programs in ${locationForHero}. These centers incorporate ${lowerCaseTerm} into their treatment plans to support individuals recovering from addiction or mental health conditions. Review our curated list to find the best rehab that meets your needs.`
    },
    'Location+treatment_services': {
      title: `Best ${secondaryTerm} Centers in ${title}`,
      description: `Our research team evaluated the <a style="text-decoration: underline;" href="#browse-all">${
        count > SIGNIFICANT_CENTER_COUNT ? count : ''
      } best treatment centers for ${lowerCaseTerm}</a> in ${locationForHero}. ${secondaryTerm} is one of many treatment services available to support you in your recovery journey. Review our curated list to find the best rehab center that meets your needs.`
    },
    'Location+activities': {
      title: `Best ${secondaryTerm} ${title} Centers`,
      description: `<span class='font-semibold'>The best rehab is one that meets your needs.</span> Our research team has compiled information on the <a style="text-decoration: underline;" href="#browse-all">${
        count > SIGNIFICANT_CENTER_COUNT ? count : ''
      } best treatment centers offering ${lowerCaseTerm} in ${locationForHero}.</a>`
    },
    'Location+setting': {
      title: `Best ${secondaryTerm} Rehabs in ${title}`,
      description: `<span class='font-semibold'>The best rehab is one that meets your needs.</span> Our research team compiled information on the <a style="text-decoration: underline;" href="#browse-all">${
        count > SIGNIFICANT_CENTER_COUNT ? count : ''
      } best treatment centers with ${lowerCaseTerm} in ${locationForHero}.</a>`
    },
    'Location+treatment_methods': {
      title: `Best ${secondaryTerm} Rehabs in ${title}`,
      description: `Our research team evaluated the <a style="text-decoration: underline;" href="#browse-all"> ${
        count > SIGNIFICANT_CENTER_COUNT ? count : ''
      } best ${lowerCaseTerm} treatment centers</a> in ${locationForHero}. ${secondaryTerm} programs can treat addiction as well as address mental health conditions like depression, anxiety, trauma, and more. Review our curated list to find the best rehab center that meets your needs.`
    }
  }

  const shouldUseLocationForHero =
    isLocationPageType(pageType) || isFacetPageType(pageType)
      ? locationForHero
      : ''

  const descriptionToRender =
    customHeroCopy ||
    getDescriptionToRender(
      copy[pageType],
      pageType,
      term,
      location,
      title,
      secondaryTerm,
      shouldDefaultRegionalize,
      count,
      shouldUseLocationForHero
    )

  const titleToRender = isLocationPageType(pageType)
    ? customTitle || copy.Location.title
    : heroHeader

  if (!copy[pageType]) {
    return null
  }

  return (
    <div>
      <div
        className={`${
          shouldUseInsuranceColor
            ? insuranceLayout[term?.slug]?.style
            : 'bg-seafoam-50'
        } pb-6 pt-4 md:pb-8 md:pt-6 lg:pb-16 lg:pt-10`}
      >
        <Helmet>
          {id ||
            (pageIsInsuranceType && (
              <link
                rel="preload"
                as="image"
                href={srcImg}
                media="(min-width: 1024px)"
                imageSizes="100vw"
              />
            ))}
        </Helmet>
        <div className="mx-auto flex items-start gap-24 px-4 md:px-6 lg:max-w-screen-xl lg:gap-16 lg:px-8 xl:gap-24">
          <div className="flex w-full flex-col gap-3 lg:grow lg:basis-0 lg:gap-6">
            <BreadCrumbV2
              title={
                title ||
                (props.pageContext &&
                  (props.pageContext[pageType] ||
                    Object.values(props.pageContext)[0])) ||
                ''
              }
              breadCrumbList={(breadcrumbs || []).map((location) => ({
                title: location
              }))}
              pageType={pageType}
              facetLocation={facetLocation}
              location={location}
              center={center}
              term={term}
            />
            <div className="flex w-full flex-col gap-4 lg:gap-6">
              {shouldUseInsuranceColor && (
                <div className="w-fit">
                  <InsuranceChip slug={term.slug} />
                </div>
              )}
              <h1
                // eslint-disable-next-line tailwindcss/no-contradicting-classname
                className={`max-w-[380px] text-balance font-FieldsVariable font-[450] text-gray-1100 md:max-w-[510px] md:font-[375] ${
                  titleToRender.length >= MAX_TITLE_LENGTH
                    ? 'text-[28px] leading-8 md:text-[40px] md:leading-[48px]'
                    : 'text-4xl leading-10 md:text-5xl md:leading-[56px]'
                }`}
              >
                {titleToRender}
              </h1>
              <p
                className="w-full text-base font-normal text-gray-900 md:max-w-[80%] lg:max-w-full"
                dangerouslySetInnerHTML={{ __html: descriptionToRender }}
              ></p>
            </div>
          </div>

          <div className="hidden w-full lg:block lg:grow lg:basis-0">
            {id && !isInsurancePage(pageType) && (
              <img
                className={`aspect-[3/2] h-auto w-full rounded-3xl border border-seafoam-200 object-cover`}
                src={srcImg}
                alt={title}
                sizes="100vw"
                fetchpriority="high"
              />
            )}
            {isInsurancePage(pageType) && (
              <img
                className={`aspect-[3/2] w-full rounded-3xl border border-white/55 object-cover`}
                src={srcImg}
                alt={title}
                sizes="100vw"
                fetchpriority="high"
              />
            )}
          </div>
        </div>
        <div className="px-4 pt-4 lg:hidden">
          <WhyTrustRecoveryMobile
            insuranceSlug={shouldUseInsuranceColor ? term?.slug : ''}
          />
        </div>
      </div>
      <div
        className={`relative -z-10 h-6 ${
          shouldUseInsuranceColor
            ? `bg-insurance-bg-${term.slug}`
            : 'bg-seafoam-50'
        } lg:hidden`}
      >
        <div className="absolute inset-0 -z-0 h-6 rounded-t-3xl bg-white lg:hidden"></div>
      </div>
    </div>
  )
}

HeroV2.propTypes = {
  background: PropTypes.string,
  title: PropTypes.string,
  count: PropTypes.number,
  pageType: PropTypes.string,
  secondaryTerm: PropTypes.string,
  customTitle: PropTypes.string,
  breadcrumbs: PropTypes.array
}

export default HeroV2
